<template>
  <div class="auth-shell">
    <div class="auth-shell__mobile-logo lg:hidden">
      <img
        src="@/assets/images/logo-tc-black.svg"
        alt="ShopiMind"
      >
    </div>

    <div
      v-if="isLoadingForm"
      class="auth-shell__loader"
    >
      <i class="pi pi-spin pi-spinner" />
    </div>

    <header class="auth-shell__header">
      <h1 class="auth-shell__title">
        {{ t('user.changePassword.title') }}
      </h1>
      <p class="auth-shell__intro">
        {{ t('user.changePassword.subTitle') }}
      </p>
    </header>

    <form
      class="auth-shell__body"
      data-test-id="form"
    >
      <div class="auth-shell__field auth-shell__field--full">
        <BaseTextInput
          v-model="userInfo.password"
          type="password"
          field-id="password"
          label="user.changePassword.password"
          input-style="width: 100%;"
          :errors="formValidation"
        />
      </div>

      <div class="auth-shell__field auth-shell__field--full">
        <BaseTextInput
          v-model="userInfo.confirm"
          type="password"
          field-id="confirm"
          label="user.changePassword.passwordConfirm"
          input-style="width: 100%;"
          :errors="formValidation"
        />
      </div>

      <SpmButton
        :label="t('validate')"
        type="button"
        :loading="isLoadingForm"
        style="width: 100%"
        class="flex p-button p-button-success auth-shell__btn"
        @click="validateRegisterForm"
      />
    </form>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import { defineComponent, reactive, ref } from 'vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { ErrorConfigForm } from '@/types/automated-scenarios';
import useVuelidate from '@vuelidate/core';
import { showToastError, showToastSuccess } from '@/helpers';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import { useRouter } from 'vue-router';
import { NestApiType, nestCall } from '@/composables/nestApi';
import {
  maxLength, minLength, required, sameAs,
} from '@vuelidate/validators';

export default defineComponent({
  name: 'ChangePassword',
  components: {
    BaseTextInput,
    SpmButton,
  },

  props: {
    token: {
      type: String,
      required: false,
      default: undefined,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const isLoadingForm = ref(false);
    const formValidation = ref<any>({});
    const token = ref(props.token);

    const userInfo = reactive({
      password: '',
      confirm: '',
    });

    const validateRegisterFormData = async (): Promise<ErrorConfigForm> => {
      const rules = {
        password: {
          required,
          minLength: minLength(8),
          maxLength: maxLength(40),
        },
        confirm: {
          required,
          sameAsPassword: sameAs(userInfo.password),
        },
      };

      const v$ = useVuelidate(rules, userInfo);
      const success = await v$.value.$validate();

      return {
        success,
        validate: v$,
      };
    };

    const validateRegisterForm = async () => {
      isLoadingForm.value = true;
      const validation = await validateRegisterFormData();
      if (!validation.success) {
        formValidation.value = validation.validate;
        isLoadingForm.value = false;
      } else {
        try {
          const result = await nestCall('post', NestApiType.AUTH, '/auth/regenerate-password', {}, '', {
            token: token.value,
            password: userInfo.password,
          }, false);

          if (result && result.success) {
            await showToastSuccess(t('user.changePassword.success'));
            await router.push('/auth/login');
          } else {
            await showToastError(t('errorMessages.GENERIC_ERROR'));
          }
        } catch (e) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
        }

        isLoadingForm.value = false;
      }
    };

    return {
      t,
      formValidation,
      userInfo,
      isLoadingForm,
      validateRegisterForm,
    };
  },
});
</script>

<style scoped lang="scss">
.auth-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 36rem;
  margin: 0 auto;
  padding: 0.5rem 0;

  &__mobile-logo {
    margin-bottom: 1.25rem;
    img { height: 28px; width: auto; }
  }

  &__loader {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    i { font-size: 1.5rem; color: #94c840; }
  }

  &__header {
    margin-bottom: 1.75rem;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 0.6rem;
    line-height: 1.25;
    letter-spacing: -0.01em;
  }

  &__intro {
    font-size: 0.92rem;
    color: #475569;
    margin: 0;
    line-height: 1.55;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  &__btn :deep(.p-button) {
    height: 2.75rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.9375rem;
    background: #94c840;
    border-color: #94c840;
    transition: all 0.2s ease;

    &:hover {
      background: #84b738;
      border-color: #84b738;
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(148, 200, 64, 0.3);
    }
    &:active { transform: translateY(0); }
  }
}

:deep(.field-wrapper .field-label) {
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #475569 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.3rem !important;
}

:deep(.p-inputtext),
:deep(.p-password-input) {
  height: 2.3rem;
  border-radius: 0.5rem;
  border-color: #d1d5db;
  font-size: 0.9rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover { border-color: #94c840; }
  &:focus,
  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
  }
}

:deep(.p-password) { width: 100%; }
</style>
